import type { ReactNode } from 'react';
import { BadgeCheck, CalendarClock, Copy, Flag, Handshake, Heart, Share2, TriangleAlert, UserPlus, Users, Zap } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { NotableFollowers } from '@/components/NotableFollowers';
import { WotGauge } from '@/components/WotGauge';
import { useToast } from '@/hooks/useToast';
import type { WotResult } from '@/lib/wot';

function httpsUrl(url: string | undefined): string | undefined {
  return url && /^https:\/\//i.test(url) ? url : undefined;
}

const compact = new Intl.NumberFormat('en', { notation: 'compact' });

function formatAge(days: number | null): string {
  if (days === null) return '—';
  if (days < 60) return `${days}d`;
  if (days < 365 * 2) return `${Math.floor(days / 30)}mo`;
  return `${(days / 365.25).toFixed(1)}y`;
}

interface StatTileProps {
  icon: ReactNode;
  label: string;
  value: string;
  sub?: string;
}

function StatTile({ icon, label, value, sub }: StatTileProps) {
  return (
    <div className="rounded-lg border border-primary/20 bg-background/60 p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export function WotReportView({ result }: { result: WotResult }) {
  const { toast } = useToast();
  const { metrics, metadata } = result;
  const npub = nip19.npubEncode(result.pubkey);

  const name =
    metadata?.display_name || metadata?.name || `${result.pubkey.slice(0, 12)}…`;
  const interactions = metrics.reactions + metrics.replies;

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied to clipboard` });
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Profile header ─────────────────────────────────────────────── */}
      <Card className="overflow-hidden bg-card/80 border-primary/30 backdrop-blur-sm py-0 gap-0">
        <div
          className="h-24 sm:h-32 bg-gradient-to-r from-primary/60 via-primary/30 to-glow/40 bg-cover bg-center"
          style={
            httpsUrl(metadata?.banner)
              ? { backgroundImage: `url(${httpsUrl(metadata?.banner)})` }
              : undefined
          }
        />
        <CardContent className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10 sm:-mt-12">
            <Avatar className="size-20 sm:size-24 border-2 border-primary/60 shadow-lg shadow-primary/30">
              <AvatarImage src={httpsUrl(metadata?.picture)} alt={name} />
              <AvatarFallback className="bg-primary/25 text-glow font-display text-2xl font-bold">
                {name.slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 sm:pb-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-2xl font-bold truncate">{name}</h2>
                {metadata?.nip05 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/15 px-2.5 py-0.5 text-xs text-glow">
                    <BadgeCheck className="size-3.5" />
                    <span className="truncate max-w-48">{metadata.nip05}</span>
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => copy(npub, 'npub')}
                className="group mt-1 inline-flex max-w-full items-center gap-1.5 rounded text-left font-mono text-xs text-muted-foreground hover:text-glow transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Copy npub"
              >
                <span className="truncate">{npub}</span>
                <Copy className="size-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="sm:mb-1 shrink-0"
              onClick={() =>
                copy(`${window.location.origin}/${npub}`, 'Report link')
              }
            >
              <Share2 className="size-4" />
              Share report
            </Button>
          </div>
          {metadata?.about && (
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">
              {metadata.about}
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── Score + stats ──────────────────────────────────────────────── */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <Card className="bg-card/80 border-primary/30 backdrop-blur-sm">
          <CardContent className="flex items-center justify-center py-8">
            <WotGauge score={result.total} grade={result.grade} />
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatTile
              icon={<Users className="size-4" />}
              label="Followers"
              value={
                metrics.followers !== null ? compact.format(metrics.followers) : '—'
              }
              sub={metrics.estimated ? 'sampled estimate' : undefined}
            />
            <StatTile
              icon={<UserPlus className="size-4" />}
              label="Following"
              value={compact.format(metrics.following)}
            />
            <StatTile
              icon={<Handshake className="size-4" />}
              label="Mutuals"
              value={
                metrics.estimatedMutuals !== null
                  ? `~${compact.format(metrics.estimatedMutuals)}`
                  : compact.format(metrics.mutualSampleCount)
              }
              sub={
                metrics.sampledFollowers > 0
                  ? `${Math.round(metrics.mutualRatio * 100)}% follow-back`
                  : undefined
              }
            />
            <StatTile
              icon={<Heart className="size-4" />}
              label="Interactions"
              value={compact.format(interactions)}
              sub={`${compact.format(metrics.replies)} replies`}
            />
            <StatTile
              icon={<Zap className="size-4" />}
              label="Zaps received"
              value={
                metrics.zapAmount > 0
                  ? `${compact.format(metrics.zapAmount)}`
                  : compact.format(metrics.zapCount)
              }
              sub={metrics.zapAmount > 0 ? `sats · ${compact.format(metrics.zapCount)} zaps` : 'zaps'}
            />
            <StatTile
              icon={<CalendarClock className="size-4" />}
              label="Account age"
              value={formatAge(metrics.accountAgeDays)}
              sub={
                metrics.providerRank !== null
                  ? `external rank ${metrics.providerRank}/100`
                  : undefined
              }
            />
          </div>

          {metrics.estimated && (
            <p className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-xs text-muted-foreground leading-relaxed">
              <TriangleAlert className="size-4 shrink-0 text-glow" />
              No NIP-85 trusted assertion was found for this identity — follower
              and engagement numbers are sampled lower-bound estimates.
            </p>
          )}
          {metrics.lowConfidence && (
            <p className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-xs text-muted-foreground leading-relaxed">
              <TriangleAlert className="size-4 shrink-0 text-glow" />
              Only a small follower sample was reachable — treat this score as
              approximate.
            </p>
          )}
          {metrics.reportsReceived > 0 && (
            <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-xs text-muted-foreground leading-relaxed">
              <Flag className="size-4 shrink-0 text-destructive" />
              {metrics.reportsReceived} report{metrics.reportsReceived === 1 ? ' has' : 's have'} been
              filed against this identity (deducted from the score).
            </p>
          )}
        </div>
      </div>

      {/* ── Breakdown ──────────────────────────────────────────────────── */}
      <Card className="bg-card/80 border-primary/30 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="font-display text-lg">How this score breaks down</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-5">
            {result.components.map((c) => (
              <li key={c.key}>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-sm font-medium">{c.label}</span>
                  <span className="font-mono text-sm text-glow tabular-nums">
                    {c.score % 1 === 0 ? c.score : c.score.toFixed(1)}
                    <span className="text-muted-foreground"> / {c.max}</span>
                  </span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-primary/15">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-glow transition-all duration-1000 ease-out motion-reduce:transition-none"
                    style={{ width: `${(c.score / c.max) * 100}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {c.description}
                </p>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* ── Notable followers ──────────────────────────────────────────── */}
      <NotableFollowers items={result.notableFollowers} />
    </div>
  );
}
